import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ToastrService} from 'ngx-toastr';

import {IEmployeeBCoin} from 'src/app/interfaces/bcoin.interface';
import {CloseHandlerService} from 'src/app/services/close-handler.service';
import {EmployeeService} from 'src/app/services/employee.service';
import {Utils} from 'src/app/shared/utils/utils';

@UntilDestroy()
@Component({
  selector: 'app-bcoin',
  templateUrl: './bcoin.component.html',
  styleUrls: ['./bcoin.component.scss'],
})
export class BcoinComponent implements OnInit {
  public bcoinData!: UntypedFormGroup;
  public employeeId!: number;
  public formValueDefault!: IEmployeeBCoin;
  public changed = false;
  public isEditable = false;
  public isEqualObjects = Utils.isEqualObjects;
  isFieldInvalid = Utils.isFieldInvalid;
  public validateAllFormFields = Utils.validateAllFormFields;

  get total(): AbstractControl {
    return this.bcoinData.get('total')!;
  }

  get spent(): AbstractControl {
    return this.bcoinData.get('spent')!;
  }

  @ViewChild('saveButton') saveButton!: ElementRef;

  constructor(
    private _fb: UntypedFormBuilder,
    private employeeService: EmployeeService,
    private route: ActivatedRoute,
    private closeHandler: CloseHandlerService,
    private notifier: ToastrService
  ) {}

  ngOnInit(): void {
    this.employeeId = +this.route.parent?.snapshot.params['id'];

    this.createFormGroup();

    this.getBcoinData();

    this.bcoinData.setValidators(this.bcoinValidator());

    this.configureCloseHandler();

    this.onDataValueChanges();
  }

  switchEdit(event: Event) {
    event.preventDefault();
    this.isEditable = !this.isEditable;
  }

  save() {
    if (this.bcoinData.invalid) {
      return this.validateAllFormFields(this.bcoinData);
    }
    let putObject: IEmployeeBCoin;
    putObject = {
      ...this.bcoinData.getRawValue(),
    };

    this.closeHandler.notSaved = false;
    this.employeeService
      .updateEmployeeBCoinScore(putObject)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isEditable = !this.isEditable;
        this.notifier.success('Saved');
      });
  }

  createFormGroup() {
    this.bcoinData = this._fb.group({
      employeeId: new UntypedFormControl(null),
      total: new UntypedFormControl(null, [
        Validators.required,
        Validators.min(0),
      ]),
      spent: new UntypedFormControl(null, [
        Validators.required,
        Validators.min(0),
      ]),
    });
  }

  getBcoinData() {
    this.employeeService
      .getEmployeeBCoinScore(this.employeeId)
      .pipe(untilDestroyed(this))
      .subscribe(data => {
        this.bcoinData.patchValue(data);
        this.formValueDefault = this.bcoinData.value;
      });
  }

  configureCloseHandler() {
    this.closeHandler.notSaveForm.pipe(untilDestroyed(this)).subscribe(() => {
      this.changed = true;
      this.bcoinData.markAsUntouched();
      this.isEditable = false;
      this.bcoinData.patchValue(this.formValueDefault);
      this.changed = false;
    });

    this.closeHandler.clickSavePopup
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.saveButton.nativeElement.click();
      });
  }

  onDataValueChanges() {
    this.bcoinData.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value: IEmployeeBCoin) => {
        if (
          value &&
          this.isEditable &&
          !this.changed &&
          !this.isEqualObjects(value, this.formValueDefault)
        ) {
          this.closeHandler.notSaved = true;
        }
      });
  }

  bcoinValidator() {
    return (): ValidationErrors | null => {
      const total = this.total.getRawValue() ?? 0;
      const spent = this.spent.getRawValue() ?? 0;

      return spent > total ? {isNotValid: true} : null;
    };
  }
}
